/**
 * Custom Upselling Awnings Section (Combined)
 * Handles travelType-based product recommendations and video playback
 *
 * travelType values:
 * - roadtrip: short trips, quick setup
 * - camping: longer stays, more comfort
 * - family: families, more space
 */
(function() {
  'use strict';

  /**
   * Initialize the upselling awnings section
   */
  function initUpsellingAwnings() {
    var sections = document.querySelectorAll('.c-upselling-awnings');

    sections.forEach(function(section) {
      initSection(section);
      initVideoPlayback(section);
    });
  }

  /**
   * Initialize a single section (product recommendations)
   * @param {HTMLElement} section - The section element
   */
  function initSection(section) {
    // Get travelType from localStorage (set by custom-product-finder)
    var travelType = localStorage.getItem('travelType');
    var options = section.querySelectorAll('.c-upselling-awnings__option');
    var defaultProductHandle = section.dataset.defaultProduct;

    if (!options.length) return;

    var recommendedOption = null;

    // Remove any existing classes from all options
    for (var i = 0; i < options.length; i++) {
      options[i].classList.remove('c-upselling-awnings__option--recommended');
      options[i].classList.remove('c-upselling-awnings__option--is-recommendation');
    }

    // Priority 0: Use override recommended product if present
    var overrideHandle = section.dataset.overrideProduct || '';
    if (!recommendedOption && overrideHandle) {
      for (var i = 0; i < options.length; i++) {
        if (options[i].dataset.productHandle === overrideHandle) {
          recommendedOption = options[i];
          break;
        }
      }
    }

    // Priority 1: Use default recommended product if configured
    if (!recommendedOption && defaultProductHandle) {
      for (var i = 0; i < options.length; i++) {
        if (options[i].dataset.productHandle === defaultProductHandle) {
          recommendedOption = options[i];
          break;
        }
      }
    }

    // Priority 2: Fall back to travelType matching if no default
    if (!recommendedOption && travelType) {
      for (var i = 0; i < options.length; i++) {
        var optionTravelType = options[i].dataset.travelType;
        if (optionTravelType && optionTravelType === travelType) {
          recommendedOption = options[i];
          break;
        }
      }
    }

    // Final fallback to first product if still no match
    if (!recommendedOption && options.length > 0) {
      recommendedOption = options[0];
    }

    // Apply classes to the recommended option:
    // --is-recommendation: marks the actual recommended product (label shows here only)
    // --recommended: marks the expanded/selected state (initially same as recommendation)
    if (recommendedOption) {
      recommendedOption.classList.add('c-upselling-awnings__option--is-recommendation');
      recommendedOption.classList.add('c-upselling-awnings__option--recommended');
      // Move recommended product to top of list
      var optionsContainer = section.querySelector('.c-upselling-awnings__options');
      if (optionsContainer && recommendedOption.parentNode === optionsContainer) {
        optionsContainer.insertBefore(recommendedOption, optionsContainer.firstChild);
      }
    }

    // Mark section as initialized
    section.dataset.initialized = 'true';

    // Set up click handlers for mobile expand (only once)
    if (!section.dataset.clickHandlersInitialized) {
      initOptionClickHandlers(section);
      section.dataset.clickHandlersInitialized = 'true';
    }

    // Listen for travelType changes (only once)
    if (!section.dataset.travelTypeListenerInitialized) {
      listenForTravelTypeChanges(section);
      section.dataset.travelTypeListenerInitialized = 'true';
    }

    // Re-run when template-customizer applies overrides (ensures recommended is moved after override is set)
    if (!section.dataset.tcDoneListenerInitialized) {
      window.addEventListener('tc:done', function reorderOnTcDone() {
        initSection(section);
      });
      section.dataset.tcDoneListenerInitialized = 'true';
    }
  }

  /**
   * Initialize click handlers for product options (mobile expand)
   * @param {HTMLElement} section - The section element
   */
  function initOptionClickHandlers(section) {
    var options = section.querySelectorAll('.c-upselling-awnings__option');

    options.forEach(function(option) {
      option.addEventListener('click', function(event) {
        // Don't trigger if clicking on the "Mehr Details" button link or Quick Add button
        if (event.target.closest('.c-upselling-awnings__option-button') ||
            event.target.closest('.c-upselling-awnings__quick-add-form')) {
          return;
        }

        var isCurrentlyExpanded = option.classList.contains('c-upselling-awnings__option--recommended');

        // Remove expanded state (--recommended) from all options
        // Note: --is-recommendation stays on the actual recommended product
        options.forEach(function(opt) {
          opt.classList.remove('c-upselling-awnings__option--recommended');
        });

        // Toggle: if clicking the same option, collapse it; otherwise expand the clicked one
        if (!isCurrentlyExpanded) {
          option.classList.add('c-upselling-awnings__option--recommended');
        }
      });
    });
  }

  /**
   * Listen for travelType changes in localStorage
   * @param {HTMLElement} section - The section element
   */
  function listenForTravelTypeChanges(section) {
    // Listen for storage events (when travelType is changed in another tab)
    window.addEventListener('storage', function(event) {
      if (event.key === 'travelType') {
        initSection(section);
      }
    });

    // Also listen for custom events that might be dispatched by the product finder
    window.addEventListener('travelTypeChanged', function() {
      initSection(section);
    });
  }

  /**
   * Initialize video playback functionality
   * @param {HTMLElement} section - The section element
   */
  function initVideoPlayback(section) {
    var videoWrapper = section.querySelector('.c-upselling-awnings__video-wrapper');
    var playButton = section.querySelector('.c-upselling-awnings__play-button');
    var video = section.querySelector('.c-upselling-awnings__video-wrapper video');

    if (!videoWrapper || !playButton || !video) return;

    // Handle play button click
    playButton.addEventListener('click', function() {
      toggleVideo(video, videoWrapper);
    });

    // Handle video click to pause
    video.addEventListener('click', function() {
      toggleVideo(video, videoWrapper);
    });

    // Update state when video ends
    video.addEventListener('ended', function() {
      videoWrapper.dataset.playing = 'false';
    });

    // Handle keyboard accessibility
    playButton.addEventListener('keydown', function(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleVideo(video, videoWrapper);
      }
    });
  }

  /**
   * Toggle video play/pause state
   * @param {HTMLVideoElement} video - The video element
   * @param {HTMLElement} wrapper - The video wrapper element
   */
  function toggleVideo(video, wrapper) {
    if (video.paused) {
      video.play();
      wrapper.dataset.playing = 'true';
    } else {
      video.pause();
      wrapper.dataset.playing = 'false';
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initUpsellingAwnings);
  } else {
    initUpsellingAwnings();
  }

  // Re-initialize on Shopify section load (Theme Editor)
  document.addEventListener('shopify:section:load', function(event) {
    var section = event.target.querySelector('.c-upselling-awnings');
    if (section) {
      initSection(section);
      initVideoPlayback(section);
    }
  });
})();
